/**
 * EmbedPage - Embeddable space viewer for iframes
 *
 * This component provides a minimal, chrome-less view of a space
 * suitable for embedding on other websites.
 *
 * URL format: /embed/{gameId}
 *
 * Query parameters:
 * - playerId: Optional player ID for interactive mode
 * - autoplay: Start playing automatically (default: false)
 * - theme: light/dark (default: dark)
 * - showControls: Show play/pause controls (default: true)
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

// 🔒 10-01 unlisted leaks (U0): how the embed names its room to the chat-game routes. A uuid route param (every embed
// code we hand out) goes by id; anything else is an old hand-built name link and keeps going by name.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function embedRoomRef(routeParam) {
  if (typeof routeParam !== 'string' || !routeParam) return null;
  if (UUID_RE.test(routeParam)) return { key: 'game_id', value: routeParam };
  let name = routeParam;
  try { name = decodeURIComponent(routeParam); } catch (e) { /* a bare % : keep it as typed */ }
  return { key: 'game_name', value: name };
}

const EmbedPage = () => {
  const { gameId } = useParams();
  const [searchParams] = useSearchParams();

  const [gameName, setGameName] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Parse options from query params
  const playerId = searchParams.get('playerId');
  const autoplay = searchParams.get('autoplay') === 'true';
  const theme = searchParams.get('theme') || 'dark';
  const showControls = searchParams.get('showControls') !== 'false';

  const isDark = theme === 'dark';

  // Styles based on theme
  const styles = {
    container: {
      height: '100vh',
      width: '100vw',
      overflow: 'hidden',
      backgroundColor: isDark ? '#1a1a2e' : '#f5f5f5',
      color: isDark ? '#fff' : '#333',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      padding: '12px 16px',
      borderBottom: `1px solid ${isDark ? '#333' : '#ddd'}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: isDark ? '#16162a' : '#fff'
    },
    title: {
      margin: 0,
      fontSize: '16px',
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px'
    },
    message: {
      marginBottom: '12px',
      padding: '10px 14px',
      borderRadius: '12px',
      backgroundColor: isDark ? '#252542' : '#fff',
      boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.1)'
    },
    messageSender: {
      fontWeight: 600,
      fontSize: '14px',
      marginBottom: '4px',
      color: isDark ? '#a5b4fc' : '#4f46e5'
    },
    messageContent: {
      fontSize: '14px',
      lineHeight: 1.5,
      color: isDark ? '#e5e7eb' : '#374151'
    },
    controls: {
      padding: '12px 16px',
      borderTop: `1px solid ${isDark ? '#333' : '#ddd'}`,
      display: 'flex',
      justifyContent: 'center',
      gap: '10px',
      backgroundColor: isDark ? '#16162a' : '#fff'
    },
    button: {
      padding: '8px 20px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: 500,
      fontSize: '14px',
      backgroundColor: isDark ? '#6366f1' : '#4f46e5',
      color: '#fff',
      transition: 'opacity 0.2s'
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    poweredBy: {
      fontSize: '11px',
      color: isDark ? '#6b7280' : '#9ca3af',
      textAlign: 'center',
      padding: '8px'
    }
  };

  // Fetch game info
  useEffect(() => {
    const fetchGameInfo = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/takeGameStep?getGameById=true&gameId=${gameId}`);

        if (response.ok) {
          const data = await response.json();
          setGameName(data.game?.name || data.name || decodeURIComponent(gameId));
          setGameData(data.game || data);
        } else {
          setGameName(decodeURIComponent(gameId));
        }
      } catch (err) {
        console.error('Error fetching game:', err);
        setGameName(decodeURIComponent(gameId));
      } finally {
        setLoading(false);
      }
    };

    fetchGameInfo();
  }, [gameId]);

  // Fetch messages
  // 🔒 10-01 unlisted leaks (U0): read and step BY the route's id. A name lookup resolves the newest room with
  // that name over HTTP, which let anyone read a hidden room by guessing its name; the backend name doors close
  // after this ships. gameName stays the header and the "info loaded" signal only. An old hand-built
  // /embed/<name> link (not a uuid) keeps its name lookup: the backend resolves a name to a PUBLIC room only, so
  // it still plays a public room and opens no hidden one. Pin: EmbedPage.test.js.
  const roomRef = embedRoomRef(gameId);
  const roomKey = roomRef ? roomRef.key : null;
  const roomValue = roomRef ? roomRef.value : null;
  const fetchMessages = useCallback(async () => {
    if (!roomKey || !gameName) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/chat-game/status?${roomKey}=${encodeURIComponent(roomValue)}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.recent_messages) {
          setMessages(data.recent_messages);
        }
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, [roomKey, roomValue, gameName]);

  // Progress game (by id, same as the read above; a name link by its name)
  const progressGame = useCallback(async () => {
    if (!roomKey || !gameName) return;

    try {
      await fetch(`${API_BASE_URL}/chat-game/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [roomKey]: roomValue })
      });
      await fetchMessages();
    } catch (err) {
      console.error('Error progressing game:', err);
    }
  }, [roomKey, roomValue, gameName, fetchMessages]);

  // Initial fetch
  useEffect(() => {
    if (gameName) {
      fetchMessages();
      if (autoplay) {
        setIsPlaying(true);
      }
    }
  }, [gameName, fetchMessages, autoplay]);

  // Polling when playing
  useEffect(() => {
    if (isPlaying && gameName) {
      const poll = async () => {
        await progressGame();
      };
      poll();
      pollIntervalRef.current = setInterval(poll, 3000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isPlaying, gameName, progressGame]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) {
    return (
      <div style={{ ...styles.container, justifyContent: 'center', alignItems: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...styles.container, justifyContent: 'center', alignItems: 'center' }}>
        <p style={{ color: '#ef4444' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>{gameName}</h1>
        {showControls && (
          <button
            style={{
              ...styles.button,
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: isPlaying ? '#ef4444' : (isDark ? '#6366f1' : '#4f46e5')
            }}
            onClick={() => setIsPlaying(!isPlaying)}
          >
            {isPlaying ? 'Pause' : 'Play'}
          </button>
        )}
      </div>

      <div style={styles.messagesContainer}>
        {messages.length === 0 ? (
          <p style={{ textAlign: 'center', color: isDark ? '#6b7280' : '#9ca3af' }}>
            No messages yet. Click Play to start.
          </p>
        ) : (
          messages.map((msg, index) => (
            <div key={index} style={styles.message}>
              {msg.sender && (
                <div style={styles.messageSender}>{msg.sender}</div>
              )}
              <div style={styles.messageContent}>{msg.message}</div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={styles.poweredBy}>
        Powered by Spaces
      </div>
    </div>
  );
};

export default EmbedPage;
